import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AppError } from '../../core/error/appError';
import { BookingValidator } from '../../core/validation/bookingValidator';
import { BookingField, type FieldErrors } from '../../core/validation/fieldError';
import { PhoneNormalizer } from '../../core/util/phoneNormalizer';
import { S } from '../../i18n/strings';
import { useSession } from '../../state/SessionContext';
import { usingFakeBackend } from '../../data/container';
import { TextField } from '../components/Fields';
import { ErrorBanner } from '../components/StateContainer';
import { CheckIcon } from '../components/icons';

/** Cada cuánto se pregunta al servidor si Telegram ya confirmó. */
const POLL_MS = 2000;

/**
 * Verificación por Telegram.
 *
 * La persona escribe su nombre, abre el enlace del bot y comparte su contacto. El teléfono no lo
 * teclea nadie: lo aporta Telegram, que ya lo verificó al registrar esa cuenta. Eso es lo que
 * separa esto de un formulario donde cualquiera pone el número de otro.
 *
 * Si el servidor todavía no tiene bot, la pantalla lo detecta y ofrece la entrada sin verificar,
 * para no quedarse sin puerta mientras se termina de montar.
 */
type Step =
  | { kind: 'NAME' }
  | { kind: 'WAITING'; token: string; url: string }
  | { kind: 'FALLBACK' };

export function LoginScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { startTelegramLink, pollTelegramLink, identify } = useSession();
  const returnTo = params.get('volver') ?? '/';

  const [step, setStep] = useState<Step>({ kind: 'NAME' });
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<AppError | null>(null);
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const done = useRef(false);

  const clearError = (field: string) =>
    setErrors((current) => {
      const { [field]: _removed, ...rest } = current;
      return rest;
    });

  const finish = useCallback(() => {
    // Se marca antes de navegar para que el sondeo en curso no vuelva a entrar.
    done.current = true;
    navigate(returnTo, { replace: true });
  }, [navigate, returnTo]);

  // Sondeo mientras la persona está en Telegram.
  useEffect(() => {
    if (step.kind !== 'WAITING') return;
    let active = true;

    const tick = async () => {
      const result = await pollTelegramLink(step.token);
      if (!active || done.current) return;

      if (!result.ok) {
        // Un fallo suelto de red no debe tirar la espera: se reintenta en el siguiente ciclo.
        if (result.error.kind !== 'NETWORK' && result.error.kind !== 'TIMEOUT') setError(result.error);
        return;
      }
      if (result.data.status === 'VERIFIED') return finish();
      if (result.data.status !== 'PENDING' && result.data.status !== 'STARTED') setExpired(true);
    };

    const timer = setInterval(tick, POLL_MS);
    tick();
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [step, pollTelegramLink, finish]);

  const submitName = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setExpired(false);

    const nameError = BookingValidator.validateName(name);
    if (nameError) return setErrors({ [BookingField.NAME]: nameError });
    setErrors({});

    setBusy(true);
    // A dónde volver desde el chat: el punto exacto del que salió, no la portada.
    const returnUrl = `${window.location.origin}${returnTo}`;
    const result = await startTelegramLink(name, returnUrl);
    setBusy(false);

    if (result.ok) return setStep({ kind: 'WAITING', token: result.data.token, url: result.data.url });

    // 503 significa "este servidor no tiene bot": se ofrece la entrada sin verificar en lugar de
    // dejar a la persona sin forma de continuar.
    if (result.error.kind === 'SERVER' && result.error.code === 503) return setStep({ kind: 'FALLBACK' });
    if (result.error.kind === 'VALIDATION') return setErrors(result.error.fields);
    setError(result.error);
  };

  const submitFallback = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const next: FieldErrors = {};
    const nameError = BookingValidator.validateName(name);
    if (nameError) next[BookingField.NAME] = nameError;
    const phoneError = BookingValidator.validatePhone(phone);
    if (phoneError) next[BookingField.PHONE] = phoneError;
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setBusy(true);
    const result = await identify(name, phone);
    setBusy(false);

    if (result.ok) return finish();
    if (result.error.kind === 'VALIDATION') setErrors(result.error.fields);
    else setError(result.error);
  };

  return (
    <div className="auth-card">
      <h1>{S.authTitle}</h1>

      {step.kind === 'NAME' && (
        <form className="stack" onSubmit={submitName}>
          <p className="muted">{S.authSubtitleTelegram}</p>
          {error && <ErrorBanner error={error} />}
          <TextField
            id="auth-name"
            label={S.bookingName}
            value={name}
            onChange={(value) => {
              setName(value);
              clearError(BookingField.NAME);
            }}
            error={errors[BookingField.NAME]}
            autoComplete="name"
          />
          <button type="submit" className="btn btn--block" disabled={busy}>
            {busy ? S.loading : S.authOpenTelegram}
          </button>
        </form>
      )}

      {step.kind === 'WAITING' && (
        <div className="stack">
          {error && <ErrorBanner error={error} />}

          {expired ? (
            <>
              <p className="banner">{S.authLinkExpired}</p>
              <button type="button" className="btn btn--block" onClick={() => setStep({ kind: 'NAME' })}>
                {S.authRetry}
              </button>
            </>
          ) : (
            <>
              {/* El enlace es el paso, no un adorno: sin abrirlo, el bot no sabe a quién escribir. */}
              <a className="btn btn--block" href={step.url} target="_blank" rel="noreferrer noopener">
                {S.authOpenTelegram}
              </a>

              <ol className="auth-steps">
                <li>{S.authStep1}</li>
                <li>{S.authStep2}</li>
                <li>{S.authStep3}</li>
              </ol>

              <p className="auth-waiting" aria-live="polite">
                <span className="auth-spinner" aria-hidden="true" />
                {S.authWaiting}
              </p>

              {usingFakeBackend && <p className="banner banner--info">{S.authDemoNotice}</p>}

              <details>
                <summary className="muted">{S.authNoTelegram}</summary>
                <p className="muted" style={{ marginTop: 8 }}>
                  {S.authNoTelegramBody}
                </p>
              </details>
            </>
          )}
        </div>
      )}

      {step.kind === 'FALLBACK' && (
        <form className="stack" onSubmit={submitFallback}>
          {/* Se dice qué se está perdiendo, en vez de degradar en silencio. */}
          <p className="banner">{S.authUnverifiedWarning}</p>
          {error && <ErrorBanner error={error} />}
          <TextField
            id="auth-name"
            label={S.bookingName}
            value={name}
            onChange={(value) => {
              setName(value);
              clearError(BookingField.NAME);
            }}
            error={errors[BookingField.NAME]}
            autoComplete="name"
          />
          <TextField
            id="auth-phone"
            label={S.bookingPhone}
            value={phone}
            onChange={(value) => {
              setPhone(value);
              clearError(BookingField.PHONE);
            }}
            error={errors[BookingField.PHONE]}
            type="tel"
            autoComplete="tel"
            placeholder="55 1234 5678"
            hint={PhoneNormalizer.isValid(phone) ? S.authPhoneConfirm(PhoneNormalizer.format(phone)) : S.authPhoneHint}
          />
          <button type="submit" className="btn btn--block" disabled={busy}>
            {busy ? S.loading : S.authContinue}
          </button>
        </form>
      )}

      {/* El descubrimiento nunca exige cuenta: siempre hay salida hacia atrás. */}
      <button type="button" className="btn btn--text" onClick={() => navigate('/')}>
        {S.authContinueGuest}
      </button>
    </div>
  );
}

/** Marca visual de que la verificación ya está hecha, para el resumen de la reserva. */
export function VerifiedBadge() {
  return (
    <span className="chip chip--static chip--open" style={{ width: 'fit-content' }}>
      <CheckIcon size={14} /> {S.authVerified}
    </span>
  );
}
